import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import API from "../lib/api";
import { CheckCircle, SpinnerGap, XCircle } from "@phosphor-icons/react";

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!orderId) { setStatus("error"); return; }

    const pollStatus = async () => {
      try {
        const { data } = await API.get(`/checkout/status/${orderId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (attempts < 8) {
          setTimeout(() => setAttempts(prev => prev + 1), 2500);
        } else {
          setStatus("pending");
        }
      } catch (err) {
        console.error("Failed to check payment status:", err);
        setStatus("error");
      }
    };

    pollStatus();
  }, [orderId, attempts]);

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 flex items-center justify-center" data-testid="checkout-success-page">
      <div className="text-center max-w-md">
        {status === "checking" && (
          <>
            <SpinnerGap size={48} weight="thin" className="text-[#D4AF37] mx-auto mb-6 animate-spin" />
            <h1 className="font-['Cormorant_Garamond'] text-3xl font-light text-[#FDFBF7] mb-4">Processing Your Order</h1>
            <p className="text-[#A1A1AA] text-sm font-light">Please wait while we confirm your payment...</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle size={48} weight="thin" className="text-[#D4AF37] mx-auto mb-6" />
            <h1 className="font-['Cormorant_Garamond'] text-3xl font-light text-[#FDFBF7] mb-4">Order Confirmed</h1>
            <p className="text-[#A1A1AA] text-sm font-light mb-8">Thank you for your purchase. Your luxury items are on their way.</p>
            <div className="flex flex-col gap-4">
              <Link to="/account" className="bg-[#D4AF37] text-black px-8 py-3 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300" data-testid="view-orders-btn">View Orders</Link>
              <Link to="/shop" className="border border-[#D4AF37] text-[#D4AF37] px-8 py-3 text-xs tracking-[0.15em] uppercase hover:bg-[#D4AF37]/10 transition-colors duration-300" data-testid="continue-shopping-after-order">Continue Shopping</Link>
            </div>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle size={48} weight="thin" className="text-red-400 mx-auto mb-6" />
            <h1 className="font-['Cormorant_Garamond'] text-3xl font-light text-[#FDFBF7] mb-4">Something Went Wrong</h1>
            <p className="text-[#A1A1AA] text-sm font-light mb-8">Please try your purchase again or contact support.</p>
            <Link to="/cart" className="bg-[#D4AF37] text-black px-8 py-3 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300">Return to Cart</Link>
          </>
        )}
        {status === "pending" && (
          <>
            <SpinnerGap size={48} weight="thin" className="text-[#D4AF37] mx-auto mb-6" />
            <h1 className="font-['Cormorant_Garamond'] text-3xl font-light text-[#FDFBF7] mb-4">Payment Processing</h1>
            <p className="text-[#A1A1AA] text-sm font-light mb-8">Your payment is still being processed. You'll receive a confirmation email once complete.</p>
            <Link to="/account" className="bg-[#D4AF37] text-black px-8 py-3 text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#E5C158] transition-colors duration-300">View Orders</Link>
          </>
        )}
      </div>
    </div>
  );
}
